// "Find my size" modal: suggests a size from height, weight and preferred fit,
// shows the garment chart in inches or centimeters and selects the size on the page.

if (!window.sizeFinderTriggerBound) {
  window.sizeFinderTriggerBound = true;
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest('[data-size-finder-open]');
    if (!trigger) return;
    // Without an id (e.g. the "Guía de ajuste" button) the page's first size finder opens.
    const id = trigger.dataset.sizeFinderOpen;
    const finder = id ? document.getElementById(id) : document.querySelector('size-finder');
    if (!finder) return;
    event.preventDefault();
    finder.open(trigger);
  });
}

if (!customElements.get('size-finder')) {
  customElements.define(
    'size-finder',
    class SizeFinder extends HTMLElement {
      static LB_PER_KG = 2.20462;
      static CM_PER_IN = 2.54;

      connectedCallback() {
        this.dialog = this.querySelector('[role="dialog"]');
        this.form = this.querySelector('.size-finder__form');
        this.result = this.querySelector('.size-finder__result');
        this.resultSize = this.querySelector('.size-finder__result-size');
        this.resultText = this.querySelector('.size-finder__result-text');
        this.selectButton = this.querySelector('.size-finder__select');
        this.units = 'imperial';
        this.sizeNames = (this.dataset.sizeNames || 'Size')
          .split(',')
          .map((name) => name.trim().toLowerCase())
          .filter(Boolean);
        try {
          this.ranges = JSON.parse(this.querySelector('[data-size-ranges]').textContent);
        } catch {
          this.ranges = [];
        }

        this.querySelectorAll('[data-size-finder-close]').forEach((element) =>
          element.addEventListener('click', () => this.close())
        );
        this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
        this.querySelectorAll('[data-go]').forEach((button) =>
          button.addEventListener('click', () => this.showView(button.dataset.go))
        );
        this.querySelectorAll('[data-unit]').forEach((button) =>
          button.addEventListener('click', () => this.setUnits(button.dataset.unit))
        );
        this.querySelectorAll('[data-chart-unit]').forEach((button) =>
          button.addEventListener('click', () => this.setChartUnit(button.dataset.chartUnit))
        );
        this.form.addEventListener('submit', (event) => {
          event.preventDefault();
          this.suggest();
        });
        this.selectButton.addEventListener('click', () => {
          this.selectSize(this.selectButton.dataset.size);
          this.close();
        });
      }

      open(trigger) {
        this.trigger = trigger;
        this.hidden = false;
        document.body.classList.add('overflow-hidden');
        // Force a reflow so the transition runs; rAF can be paused in background tabs.
        void this.offsetWidth;
        this.classList.add('is-open');
        if (typeof trapFocus === 'function') trapFocus(this.dialog);
      }

      close() {
        this.classList.remove('is-open');
        document.body.classList.remove('overflow-hidden');
        if (typeof removeTrapFocus === 'function') removeTrapFocus(this.trigger);
        setTimeout(() => (this.hidden = true), 200);
      }

      showView(name) {
        this.querySelectorAll('.size-finder__view').forEach((view) => (view.hidden = view.dataset.view !== name));
        this.querySelectorAll('.size-finder__choice').forEach((choice) => {
          const active = choice.dataset.go === name;
          choice.classList.toggle('is-active', active);
          choice.setAttribute('aria-pressed', String(active));
        });
        this.dialog.scrollTop = 0;
      }

      toggleGroup(buttons, activeButton) {
        buttons.forEach((button) => {
          const active = button === activeButton;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
      }

      setUnits(units) {
        if (units === this.units) return;
        const values = this.readInputs();
        this.units = units;
        this.toggleGroup(this.querySelectorAll('[data-unit]'), this.querySelector(`[data-unit="${units}"]`));
        this.querySelectorAll('[data-units]').forEach((group) => (group.hidden = group.dataset.units !== units));
        this.querySelector('[data-weight-unit]').textContent = units === 'metric' ? '(kg)' : '(lb)';

        // Keep what the shopper typed, converted to the new units.
        const field = (name) => this.form.elements[name];
        if (units === 'metric') {
          field('cm').value = Math.round(values.heightIn * SizeFinder.CM_PER_IN);
          field('weight').value = Math.round(values.weightLb / SizeFinder.LB_PER_KG);
        } else {
          field('feet').value = Math.floor(values.heightIn / 12);
          field('inches').value = Math.round(values.heightIn % 12);
          field('weight').value = Math.round(values.weightLb);
        }
      }

      setChartUnit(unit) {
        this.toggleGroup(this.querySelectorAll('[data-chart-unit]'), this.querySelector(`[data-chart-unit="${unit}"]`));
        this.querySelectorAll('.size-finder__table td[data-inches]').forEach((cell) => {
          const inches = parseFloat(cell.dataset.inches);
          if (Number.isNaN(inches)) return;
          cell.textContent = unit === 'cm' ? (inches * SizeFinder.CM_PER_IN).toFixed(1) : cell.dataset.inches;
        });
      }

      readInputs() {
        const value = (name) => parseFloat(this.form.elements[name].value) || 0;
        if (this.units === 'metric') {
          return { heightIn: value('cm') / SizeFinder.CM_PER_IN, weightLb: value('weight') * SizeFinder.LB_PER_KG };
        }
        return { heightIn: value('feet') * 12 + value('inches'), weightLb: value('weight') };
      }

      // Weight picks the starting size; tall shoppers in the upper half of a range size up,
      // "closer" sizes down near the bottom of a range and "oversized" adds one size.
      // Near a range edge the neighbouring size is returned too, so we can say "between S / M".
      recommend({ heightIn, weightLb }, fit) {
        const ranges = this.ranges;
        if (!ranges.length || !weightLb) return null;
        const last = ranges.length - 1;
        const clamp = (index) => Math.max(0, Math.min(last, index));

        let base = ranges.findIndex((range) => weightLb >= range.min && weightLb <= range.max);
        if (base === -1) base = weightLb < ranges[0].min ? 0 : last;

        const range = ranges[base];
        const position = (weightLb - range.min) / Math.max(1, range.max - range.min);
        const tall = parseFloat(this.dataset.tall) || 74;

        let index = base;
        if (heightIn >= tall && position >= 0.5) index++;
        if (fit === 'closer' && position < 0.35) index--;
        if (fit === 'oversized') index++;
        index = clamp(index);

        let neighbour = null;
        if (position <= 0.25 && base > 0) neighbour = base - 1;
        if (position >= 0.75 && base < last) neighbour = base + 1;
        if (neighbour === index) neighbour = base;

        const between =
          neighbour !== null && neighbour !== index
            ? [ranges[Math.min(index, neighbour)].size, ranges[Math.max(index, neighbour)].size]
            : null;

        return { size: ranges[index].size, between };
      }

      suggest() {
        const fit = this.form.elements.fit.value;
        const match = this.recommend(this.readInputs(), fit);
        this.result.hidden = false;

        if (!match) {
          this.resultSize.textContent = '';
          this.resultText.textContent = this.dataset.noResult;
          this.selectButton.hidden = true;
          return;
        }

        const { size, between } = match;
        this.resultSize.textContent = size;
        const template = between ? this.dataset.overlap : this.dataset.single;
        this.resultText.textContent = template
          .replaceAll('[size]', size)
          .replaceAll('[sizes]', between ? between.join(' / ') : size);

        const available = Boolean(this.findSizeInput(size));
        this.selectButton.hidden = !available;
        this.selectButton.dataset.size = size;
        this.selectButton.textContent = this.dataset.select.replace('[size]', size);
      }

      // Finds the size option's radio input or <option> for this value in the section's variant picker.
      findSizeInput(size) {
        const section = this.id.replace('SizeFinder-', '');
        const picker = document.getElementById(`variant-selects-${section}`);
        if (!picker) return null;
        const wanted = size.trim().toLowerCase();

        for (const input of picker.querySelectorAll('input[type="radio"], select option')) {
          const optionName = (input.dataset.optionName || '').trim().toLowerCase();
          if (this.sizeNames.includes(optionName) && input.value.trim().toLowerCase() === wanted) return input;
        }
        return null;
      }

      selectSize(size) {
        const input = this.findSizeInput(size);
        if (!input) return;
        if (input.tagName === 'OPTION') {
          const select = input.closest('select');
          select.value = input.value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else if (!input.checked) {
          input.click();
        }
      }
    }
  );
}
